import React, {
  useState,
  ReactElement,
  RefObject,
  useCallback,
  useRef,
  useMemo,
  createContext,
  Dispatch,
  useContext,
  SetStateAction,
  useEffect,
} from 'react';
import { format, isEqual, isValid } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useOutsideClickListener } from '../../../hooks/useOutsideClickListener';
import { useKeyPress } from '../../../hooks/useKeyPress';
import { Container } from './styled';
import { getDateTimeFormatString, is12Hour } from './util';
import { DateTimeTextInput } from './components/DateTimeTextInput';
import { DateTimeTextInputControls } from './components/DateTimeTextInputControls';
import { DateTimeCalendarInput } from './components/DateTimeCalendarInput';
import { InputError } from '../InputError';
import { Merge } from '../../../utilities/types';

export type DateTimeInputProps = Merge<
  React.DetailedHTMLProps<React.InputHTMLAttributes<any>, HTMLInputElement>,
  {
    ref?:
      | ((instance: HTMLInputElement | null) => void)
      | RefObject<HTMLInputElement>
      | null
      | undefined;
    value?: Date;
    name?: string;
    prefix?: ReactElement;
    label?: string;
    disabled?: boolean;
    dataCy?: string;
    error?: string;
    minDate?: Date;
    maxDate?: Date;
    timeZone?: string;
    hideTime?: boolean;
    hideDate?: boolean;
    className?: string;
    calendarAlignment?: 'top' | 'bottom' | 'center';
    onChange: (date?: Date) => void;
    onBlur?: (e: React.FocusEvent) => void;
    t?: (key: string) => string;
  }
>;

// If more properties are added consider grouping them in options
interface DateTimeInputContextValue {
  onChange: Dispatch<Date | undefined>;
  onBlur?: Dispatch<React.FocusEvent>;
  setInput: Dispatch<SetStateAction<string | undefined>>;
  setInputDate: Dispatch<SetStateAction<Date | undefined>>;
  setIsActive: Dispatch<SetStateAction<boolean>>;
  close: () => void;
  value?: Date;
  disabled?: boolean;
  error?: string;
  input?: string;
  inputDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  label?: string;
  isActive?: boolean;
  hideTime?: boolean;
  hideDate?: boolean;
  use12HourMode?: boolean;
  dateTimeFormat: string;
  timeZone?: string;
  containerRef: RefObject<HTMLHeadingElement>;
  calendarAlignment?: 'top' | 'bottom' | 'center';
  t?: (key: string) => string;
}

const DateTimeInputContext = createContext<DateTimeInputContextValue | null>(
  null,
);

export const useDateTimeInput = () => {
  const context = useContext(DateTimeInputContext);

  if (!context)
    throw new Error('useDateTimeInput can only be used inside DateTimeInput');

  return context;
};

export const DateTimeInput: React.FC<DateTimeInputProps> = ({
  name,
  value,
  prefix,
  label,
  onBlur,
  onChange,
  disabled,
  dataCy,
  error,
  minDate,
  maxDate,
  timeZone,
  hideTime,
  hideDate,
  calendarAlignment,
  className,
  t,
  ...inputProps
}) => {
  const containerRef = useRef<HTMLHeadingElement>(null);
  const use12HourMode = is12Hour(navigator.language);
  const dateTimeFormat = getDateTimeFormatString(navigator.language, hideTime);

  const [isActive, setIsActive] = useState(false);
  const [input, setInput] = useState<string | undefined>(() =>
    getInitialInputValue(value, dateTimeFormat, timeZone),
  );
  const [inputDate, setInputDate] = useState(
    getInitialInputDateValue(value, timeZone),
  );

  const close = useCallback(() => setIsActive(false), [setIsActive]);

  useOutsideClickListener(containerRef, close);
  useKeyPress({ targetKey: 'Escape', onPress: close });

  useEffect(() => {
    if (value && isValid(value)) {
      const zonedDate =
        timeZone && value ? utcToZonedTime(value, timeZone) : value;

      if (!inputDate || !isEqual(zonedDate, inputDate)) {
        setInputDate(zonedDate);
        setInput(format(zonedDate, dateTimeFormat));
      }
    } else if (!value) {
      setInputDate(undefined);
      setInput('');
    }
  }, [value, timeZone, dateTimeFormat, inputDate]);

  const contextValue = useMemo(
    () => ({
      containerRef,
      close,
      isActive,
      setIsActive,
      dateTimeFormat,
      input,
      setInput,
      inputDate,
      setInputDate,
      value,
      onChange,
      onBlur,
      disabled,
      error,
      use12HourMode,
      minDate,
      maxDate,
      hideTime,
      hideDate,
      calendarAlignment,
      name,
      prefix,
      label,
      timeZone,
      t,
    }),
    [
      close,
      dateTimeFormat,
      isActive,
      setIsActive,
      input,
      setInput,
      inputDate,
      setInputDate,
      value,
      onChange,
      onBlur,
      disabled,
      error,
      use12HourMode,
      minDate,
      maxDate,
      hideTime,
      hideDate,
      calendarAlignment,
      name,
      prefix,
      label,
      timeZone,
      t,
    ],
  );

  return (
    <DateTimeInputContext.Provider value={contextValue}>
      <Container ref={containerRef} className={className} data-cy={dataCy}>
        <DateTimeTextInput prefix={prefix} {...inputProps} />
        <DateTimeTextInputControls />
        {!!error && <InputError>{error}</InputError>}
        <DateTimeCalendarInput />
      </Container>
    </DateTimeInputContext.Provider>
  );
};

function getInitialInputValue(
  value: Date | undefined,
  dateFormat: string,
  timeZone?: string,
): string {
  if (value && isValid(value)) {
    return format(
      timeZone ? utcToZonedTime(value, timeZone) : value,
      dateFormat,
    );
  }

  return '';
}

function getInitialInputDateValue(
  value: Date | undefined,
  timeZone?: string,
): Date | undefined {
  if (value && isValid(value)) {
    return timeZone ? utcToZonedTime(value, timeZone) : value;
  }

  return undefined;
}
